<template>
  <div class="min-h-screen bg-[#f6f7f9]">

    <!-- ── Hero ── -->
    <section class="bg-white border-b border-[#f0f0f0] py-12 lg:py-16">
      <div class="max-w-screen-xl mx-auto px-6">
        <!-- Breadcrumb -->
        <nav class="flex items-center gap-1.5 text-[0.8rem] text-[#999] mb-6 flex-wrap">
          <router-link to="/" class="hover:text-[#111] transition-colors no-underline"><i class="bi bi-house"></i></router-link>
          <i class="bi bi-chevron-right text-[0.6rem]"></i>
          <span class="text-[#333] font-medium">Installatie</span>
        </nav>

        <div class="flex flex-col lg:flex-row gap-10 items-center">
          <div class="flex-1">
            <span class="inline-flex items-center gap-1.5 text-[0.75rem] font-bold uppercase tracking-wider text-[#7a5f00] bg-[#fffbe6] border border-[#FFD500] px-3 py-1 rounded-full mb-4">
              <i class="bi bi-tools"></i> Installatiegids
            </span>
            <h1 class="text-[clamp(1.75rem,4vw,2.75rem)] font-black text-[#111] leading-tight mb-4">
              Zonnepanelen installeren: zo doe je dat
            </h1>
            <p class="text-[1.0625rem] text-[#555] leading-relaxed mb-6 max-w-[560px]">
              Balkonzonnepanelen installeer je in minder dan 30 minuten. Volg onze stap-voor-stap handleiding. Kom je er toch niet uit? Dan regelt Plug2Charge het voor je.
            </p>
            <div class="flex flex-wrap gap-3">
              <a href="#stappen" class="inline-flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-5 py-3 rounded-xl no-underline transition-all text-[0.9375rem]">
                <i class="bi bi-lightning-charge"></i> Bekijk de handleiding
              </a>
              <a href="#service" class="inline-flex items-center gap-2 bg-white hover:bg-[#f8f9fa] border border-[#e5e7eb] text-[#111] font-semibold px-5 py-3 rounded-xl no-underline transition-all text-[0.9375rem]">
                <i class="bi bi-person-gear"></i> Laat het installeren
              </a>
            </div>
          </div>

          <!-- Stats blokjes -->
          <div class="grid grid-cols-2 gap-3 shrink-0">
            <div class="bg-[#f8f9fa] rounded-2xl p-5 text-center">
              <div class="text-[2rem] font-black text-[#111] leading-none mb-1">30</div>
              <div class="text-[0.78rem] text-[#888] font-medium">minuten installatie</div>
            </div>
            <div class="bg-[#FFD500] rounded-2xl p-5 text-center">
              <div class="text-[2rem] font-black text-[#111] leading-none mb-1">0</div>
              <div class="text-[0.78rem] text-[#111]/60 font-medium">bouwkundige ingrepen</div>
            </div>
            <div class="bg-[#f8f9fa] rounded-2xl p-5 text-center">
              <div class="text-[2rem] font-black text-[#111] leading-none mb-1">800W</div>
              <div class="text-[0.78rem] text-[#888] font-medium">vergunningsvrij</div>
            </div>
            <div class="bg-[#111] rounded-2xl p-5 text-center">
              <div class="text-[2rem] font-black text-white leading-none mb-1">✓</div>
              <div class="text-[0.78rem] text-white/60 font-medium">geen elektricien nodig</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Stap-voor-stap handleiding ── -->
    <section id="stappen" class="py-14 lg:py-20">
      <div class="max-w-screen-xl mx-auto px-6">

        <div class="text-center mb-12">
          <h2 class="text-[clamp(1.5rem,3vw,2.25rem)] font-black text-[#111] mb-3">In 5 stappen klaar</h2>
          <p class="text-[#666] text-[1rem] max-w-[480px] mx-auto">Volg deze stappen en je balkonsysteem wekt vandaag al stroom op.</p>
        </div>

        <div class="flex flex-col gap-4 max-w-3xl mx-auto">
          <div
            v-for="(step, i) in steps"
            :key="i"
            class="bg-white rounded-2xl border border-[#e9eaec] overflow-hidden cursor-pointer"
            @click="openStep = openStep === i ? null : i"
          >
            <div class="flex items-center gap-4 px-6 py-5">
              <div class="w-10 h-10 rounded-xl flex items-center justify-center text-[0.9rem] font-black shrink-0"
                :class="openStep === i ? 'bg-[#FFD500] text-[#111]' : 'bg-[#f3f4f6] text-[#888]'">
                {{ i + 1 }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="font-bold text-[#111] text-[0.9375rem] leading-snug">{{ step.title }}</p>
                <p class="text-[0.8rem] text-[#888] mt-0.5">{{ step.sub }}</p>
              </div>
              <i :class="['bi text-[#aaa] text-[1rem] transition-transform shrink-0',
                openStep === i ? 'bi-chevron-up text-[#FFD500]' : 'bi-chevron-down']"></i>
            </div>

            <transition name="accordion">
              <div v-if="openStep === i" class="border-t border-[#f3f4f6]">
                <div class="px-6 py-5 flex flex-col gap-5">
                  <div>
                    <p class="text-[0.9rem] text-[#444] leading-relaxed mb-3" v-html="step.body"></p>
                    <ul v-if="step.tips" class="flex flex-col gap-1.5">
                      <li v-for="tip in step.tips" :key="tip"
                        class="flex items-start gap-2 text-[0.85rem] text-[#555]">
                        <i class="bi bi-check2-circle text-[#FFD500] mt-0.5 shrink-0"></i>
                        {{ tip }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="step.warning" class="bg-[#fffbe6] border border-[#FFD500]/40 rounded-xl p-4 min-w-[220px]">
                    <p class="text-[0.78rem] font-bold text-[#7a5f00] mb-1 flex items-center gap-1.5">
                      <i class="bi bi-exclamation-triangle"></i> Let op
                    </p>
                    <p class="text-[0.78rem] text-[#7a5f00]">{{ step.warning }}</p>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Veelgestelde vragen ── -->
    <section class="bg-white border-t border-b border-[#f0f0f0] py-14">
      <div class="max-w-3xl mx-auto px-6">
        <h2 class="text-[1.5rem] font-black text-[#111] mb-8 text-center">Veelgestelde vragen</h2>
        <div class="flex flex-col divide-y divide-[#f3f4f6]">
          <div v-for="(faq, i) in faqs" :key="i" class="py-4 cursor-pointer" @click="openFaq = openFaq === i ? null : i">
            <div class="flex items-center justify-between gap-4">
              <p class="font-semibold text-[0.9375rem] text-[#111]">{{ faq.q }}</p>
              <i :class="['bi shrink-0 text-[#aaa]', openFaq === i ? 'bi-dash' : 'bi-plus']"></i>
            </div>
            <transition name="accordion">
              <p v-if="openFaq === i" class="text-[0.875rem] text-[#555] leading-relaxed mt-3 pr-8">{{ faq.a }}</p>
            </transition>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Installatieservice funnel ── -->
    <section id="service" class="py-14 lg:py-20">
      <div class="max-w-screen-xl mx-auto px-6">

        <div class="bg-[#111] rounded-3xl overflow-hidden">
          <div class="grid lg:grid-cols-2 gap-0">

            <!-- Links: uitleg -->
            <div class="p-8 lg:p-12 flex flex-col justify-center">
              <span class="inline-flex items-center gap-1.5 text-[0.72rem] font-bold uppercase tracking-widest text-[#FFD500] mb-4">
                <i class="bi bi-person-gear"></i> Professionele installatieservice
              </span>
              <h2 class="text-[clamp(1.5rem,3vw,2.25rem)] font-black text-white leading-tight mb-4">
                Kom je er niet uit? Wij installeren het voor je.
              </h2>
              <p class="text-white/60 text-[0.9375rem] leading-relaxed mb-8">
                Onze gecertificeerde installateurs regelen alles: van bevestiging op het balkon of dak tot aansluiting en app-configuratie. Jij hoeft er niets aan te doen.
              </p>
              <ul class="flex flex-col gap-3">
                <li v-for="usp in serviceUsps" :key="usp" class="flex items-center gap-3 text-[0.875rem] text-white/80">
                  <div class="w-6 h-6 rounded-full bg-[#FFD500] flex items-center justify-center shrink-0">
                    <i class="bi bi-check text-[#111] text-[0.7rem] font-black"></i>
                  </div>
                  {{ usp }}
                </li>
              </ul>
            </div>

            <!-- Rechts: funnel form -->
            <div class="bg-white p-8 lg:p-10 flex flex-col">

              <!-- Stap indicators -->
              <div class="flex items-center gap-0 mb-8">
                <template v-for="(label, i) in funnelStepLabels" :key="i">
                  <div class="flex items-center gap-1.5">
                    <div class="w-6 h-6 rounded-full text-[0.7rem] font-black flex items-center justify-center shrink-0 transition-all"
                      :class="funnelStep > i ? 'bg-[#FFD500] text-[#111]' : funnelStep === i ? 'bg-[#111] text-white' : 'bg-[#f3f4f6] text-[#aaa]'">
                      <i v-if="funnelStep > i" class="bi bi-check text-[0.6rem]"></i>
                      <span v-else>{{ i + 1 }}</span>
                    </div>
                    <span class="text-[0.72rem] font-semibold hidden sm:inline"
                      :class="funnelStep === i ? 'text-[#111]' : 'text-[#aaa]'">{{ label }}</span>
                  </div>
                  <div v-if="i < funnelStepLabels.length - 1" class="flex-1 h-px mx-2 transition-colors"
                    :class="funnelStep > i ? 'bg-[#FFD500]' : 'bg-[#f3f4f6]'"></div>
                </template>
              </div>

              <!-- Stap 0: Wat wil je installeren? -->
              <div v-if="funnelStep === 0" class="flex flex-col gap-3 flex-1">
                <h3 class="font-bold text-[#111] text-[1.0625rem] mb-1">Wat wil je laten installeren?</h3>
                <label v-for="opt in installTypes" :key="opt.value"
                  @click="form.type = opt.value"
                  :class="['flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
                    form.type === opt.value ? 'border-[#FFD500] bg-[#fffbe6]' : 'border-[#e5e7eb] hover:border-[#FFD500]/50']">
                  <div class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    :class="form.type === opt.value ? 'bg-[#FFD500] text-[#111]' : 'bg-[#f8f9fa] text-[#aaa]'">
                    <i :class="['bi text-[1.1rem]', opt.icon]"></i>
                  </div>
                  <div class="flex-1">
                    <p class="font-semibold text-[#111] text-[0.875rem]">{{ opt.label }}</p>
                    <p class="text-[0.775rem] text-[#888]">{{ opt.sub }}</p>
                  </div>
                  <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0"
                    :class="form.type === opt.value ? 'border-[#FFD500] bg-[#FFD500]' : 'border-[#ddd]'">
                    <i v-if="form.type === opt.value" class="bi bi-check text-[0.6rem] text-[#111] font-black"></i>
                  </div>
                </label>
                <button @click="funnelStep = 1" :disabled="!form.type"
                  class="mt-auto w-full bg-[#FFD500] hover:bg-[#ffb800] disabled:opacity-40 disabled:cursor-not-allowed text-[#111] font-bold py-3 rounded-xl transition-all">
                  Volgende stap <i class="bi bi-arrow-right ml-1"></i>
                </button>
              </div>

              <!-- Stap 1: Situatie -->
              <div v-if="funnelStep === 1" class="flex flex-col gap-3 flex-1">
                <h3 class="font-bold text-[#111] text-[1.0625rem] mb-1">Wat is jouw situatie?</h3>
                <label v-for="opt in situationTypes" :key="opt.value"
                  @click="form.situation = opt.value"
                  :class="['flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
                    form.situation === opt.value ? 'border-[#FFD500] bg-[#fffbe6]' : 'border-[#e5e7eb] hover:border-[#FFD500]/50']">
                  <div class="w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center shrink-0"
                    :class="form.situation === opt.value ? 'border-[#FFD500] bg-[#FFD500]' : 'border-[#ddd]'">
                    <i v-if="form.situation === opt.value" class="bi bi-check text-[0.6rem] text-[#111]"></i>
                  </div>
                  <div>
                    <p class="font-semibold text-[#111] text-[0.875rem]">{{ opt.label }}</p>
                    <p class="text-[0.775rem] text-[#888]">{{ opt.sub }}</p>
                  </div>
                </label>
                <div class="flex gap-3 mt-auto">
                  <button @click="funnelStep = 0" class="px-4 py-3 rounded-xl border border-[#e5e7eb] text-[#666] font-semibold hover:border-[#ddd] transition-all text-[0.875rem]">
                    <i class="bi bi-arrow-left"></i>
                  </button>
                  <button @click="funnelStep = 2" :disabled="!form.situation"
                    class="flex-1 bg-[#FFD500] hover:bg-[#ffb800] disabled:opacity-40 disabled:cursor-not-allowed text-[#111] font-bold py-3 rounded-xl transition-all">
                    Volgende <i class="bi bi-arrow-right ml-1"></i>
                  </button>
                </div>
              </div>

              <!-- Stap 2: Contactgegevens -->
              <div v-if="funnelStep === 2" class="flex flex-col gap-4 flex-1">
                <input v-model="hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true" class="hidden" />
                <h3 class="font-bold text-[#111] text-[1.0625rem] mb-1">Jouw gegevens</h3>
                <div class="grid grid-cols-2 gap-3">
                  <div class="flex flex-col gap-1">
                    <label class="text-[0.78rem] font-semibold text-[#555]">Voornaam *</label>
                    <input v-model="form.name" type="text" placeholder="Thomas" class="border border-[#e5e7eb] rounded-lg px-3 py-2.5 text-[0.875rem] outline-none focus:border-[#FFD500] transition-colors">
                  </div>
                  <div class="flex flex-col gap-1">
                    <label class="text-[0.78rem] font-semibold text-[#555]">Postcode *</label>
                    <input v-model="form.postcode" type="text" placeholder="1234 AB" class="border border-[#e5e7eb] rounded-lg px-3 py-2.5 text-[0.875rem] outline-none focus:border-[#FFD500] transition-colors">
                  </div>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-[0.78rem] font-semibold text-[#555]">E-mailadres *</label>
                  <input v-model="form.email" type="email" placeholder="thomas@email.nl" class="border border-[#e5e7eb] rounded-lg px-3 py-2.5 text-[0.875rem] outline-none focus:border-[#FFD500] transition-colors">
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-[0.78rem] font-semibold text-[#555]">Telefoon</label>
                  <input v-model="form.phone" type="tel" placeholder="+31 6 12345678" class="border border-[#e5e7eb] rounded-lg px-3 py-2.5 text-[0.875rem] outline-none focus:border-[#FFD500] transition-colors">
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-[0.78rem] font-semibold text-[#555]">Opmerking (optioneel)</label>
                  <textarea v-model="form.note" rows="3" placeholder="Bijv. type panelen, situatie op het balkon…" class="border border-[#e5e7eb] rounded-lg px-3 py-2.5 text-[0.875rem] outline-none focus:border-[#FFD500] transition-colors resize-none"></textarea>
                </div>
                <p v-if="submitError" class="flex items-start gap-2 text-[0.82rem] text-[#b42318] bg-[#fef3f2] border border-[#fecdca] rounded-lg px-3 py-2.5" role="alert">
                  <i class="bi bi-exclamation-triangle-fill mt-0.5"></i>
                  <span>{{ submitError }}</span>
                </p>
                <div class="flex gap-3 mt-auto">
                  <button @click="funnelStep = 1" class="px-4 py-3 rounded-xl border border-[#e5e7eb] text-[#666] font-semibold hover:border-[#ddd] transition-all text-[0.875rem]">
                    <i class="bi bi-arrow-left"></i>
                  </button>
                  <button @click="submitFunnel" :disabled="!canSubmit || submitting"
                    class="flex-1 bg-[#FFD500] hover:bg-[#ffb800] disabled:opacity-40 disabled:cursor-not-allowed text-[#111] font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2">
                    <i :class="['bi', submitting ? 'bi-arrow-repeat' : 'bi-send']"></i>
                    {{ submitting ? 'Verzenden…' : (submitError ? 'Opnieuw proberen' : 'Aanvraag versturen') }}
                  </button>
                </div>
              </div>

              <!-- Bevestiging -->
              <div v-if="funnelStep === 3" class="flex flex-col items-center justify-center text-center flex-1 py-4">
                <div class="w-16 h-16 rounded-2xl bg-[#FFD500] flex items-center justify-center mb-5">
                  <i class="bi bi-check-lg text-[2rem] text-[#111] font-black"></i>
                </div>
                <h3 class="text-[1.25rem] font-black text-[#111] mb-2">Aanvraag ontvangen!</h3>
                <p class="text-[0.9rem] text-[#666] leading-relaxed max-w-[320px]">
                  We nemen binnen <strong>1 werkdag</strong> contact met je op om een afspraak in te plannen.
                </p>
                <p class="text-[0.8rem] text-[#aaa] mt-3">Controleer ook je spammap.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

// ── Stap-voor-stap handleiding ────────────────────────────────────────────────
const openStep = ref(0)

const steps = [
  {
    title: 'Kies de juiste locatie op je balkon',
    sub:   'Zuid, oost of west, allemaal geschikt',
    body:  'Hang de panelen zo op dat ze zo veel mogelijk direct zonlicht vangen. Een zuidelijke oriëntatie geeft de hoogste opbrengst, maar ook oost (ochtendzon) of west (middagzon) werkt goed.',
    tips:  [
      'Controleer of de balkonreling stevig genoeg is voor het gewicht (±11 kg per paneel)',
      'Houd minimaal 30 cm vrije ruimte rondom het paneel voor luchtcirculatie',
      'Vermijd schaduw van antennes, schoorstenen of dakranden',
    ],
    warning: 'Gebruik altijd de meegeleverde bevestigingshoeken. Eigen constructies kunnen de garantie doen vervallen.',
  },
  {
    title: 'Bevestig het paneel aan de balkonreling',
    sub:   'Klemmen, haken of bouten, afhankelijk van je set',
    body:  'De meeste balkonsystemen worden bevestigd met klemmen die over de reling klemmen. Geen boren of permanente bevestiging nodig. Zet de klemmen stevig aan met de meegeleverde moersleutel.',
    tips:  [
      'Kantel het paneel op de optimale hoek (25°–40° voor Nederland)',
      'Controleer of het paneel niet kan wegwaaien bij storm',
      'Zorg dat het paneel niet buiten de reling uitsteekt',
    ],
  },
  {
    title: 'Sluit de DC-kabel aan op de micro-omvormer',
    sub:   'MC4-connectoren klikken gewoon vast',
    body:  'Sluit de positieve (+) en negatieve (-) kabel van het zonnepaneel aan op de bijbehorende MC4-connectoren van de micro-omvormer. Je hoort een klik als ze goed vast zitten.',
    tips:  [
      'Plus op plus, min op min: de connectoren passen maar op één manier',
      'Trek lichtjes aan de kabel na het klikken om te testen of hij vast zit',
      'Laat geen losse kabeluiteinden vrij rondhangen',
    ],
    warning: 'Raak de DC-aansluitingen niet aan als het paneel zonlicht ontvangt. Er staat dan spanning op.',
  },
  {
    title: 'Sluit de micro-omvormer aan op het stopcontact',
    sub:   'Schuko stekker in een gewone wandcontactdoos',
    body:  'Verbind de AC-kabel (wisselstroom) van de micro-omvormer met het meegeleverde Schuko-snoer en steek dit in een gewone wandcontactdoos. De omvormer begint automatisch te werken zodra er genoeg zon is.',
    tips:  [
      'Gebruik bij voorkeur een buitenstopcontact of een stopcontact in de buurt van de deur',
      'De kabel mag niet over de grond lopen; bevestig hem netjes langs de wand',
      'Controleer of de contactdoos geschikt is voor buitengebruik (IP44)',
    ],
    warning: 'Maximaal 800 Wp per groep op een gewone storingzekering. Gebruik bij twijfel een aparte groep.',
  },
  {
    title: 'Verbind de omvormer met de S-Miles Cloud app',
    sub:   'WiFi-configuratie via je smartphone',
    body:  'Download de S-Miles Cloud app (iOS / Android) en volg de configuratiewizard. De omvormer maakt tijdelijk een eigen WiFi-netwerk aan - verbind hiermee en voer je thuisnetwerk-wachtwoord in. Daarna zie je real-time je opbrengst, vermogen en energieproductie.',
    tips:  [
      'Zorg dat je smartphone op hetzelfde WiFi-netwerk zit als je thuis gebruikt',
      'Het instellen duurt ±5 minuten via de app',
      'Je kunt meerdere omvormers op één account beheren',
    ],
  },
]

// ── FAQ ───────────────────────────────────────────────────────────────────────
const openFaq = ref(null)

const faqs = [
  {
    q: 'Heb ik een vergunning nodig voor balkonsystemen?',
    a: 'In de meeste Nederlandse gemeenten zijn balkonsystemen tot 800 Wp vergunningsvrij als je ze niet permanent bevestigt. Controleer wel even de regels van je gemeente of VvE, want die kunnen afwijken.',
  },
  {
    q: 'Mag ik balkonsystemen plaatsen als ik huur?',
    a: 'Ja, in principe wel. Balkonsystemen worden niet vast bevestigd en laten geen schade achter. Informeer je verhuurder uit beleefdheid, maar wettelijk is er geen bezwaar voor tijdelijke systemen die je meeneemt bij vertrek.',
  },
  {
    q: 'Hoeveel stroom wekt een balkonsysteem op?',
    a: 'Een systeem van 400 Wp op een zuidgericht balkon wekt in Nederland gemiddeld 300–380 kWh per jaar op. Bij €0,28/kWh is dat €85–€106 besparing per jaar. Op een oost- of westgericht balkon is dit zo\'n 15–25% minder.',
  },
  {
    q: 'Kan ik de stroom opslaan in een batterij?',
    a: 'Balkonsystemen voeden direct het stroomnet van je woning in. Je verbruikt de opgewekte stroom meteen (verlicht echt je meter). Wil je stroom opslaan? Dan heb je een thuisbatterij nodig die aansluit op je omvormer.',
  },
  {
    q: 'Wat als ik meer vermogen wil dan 800 Wp?',
    a: 'Boven de 800 Wp moet je het systeem aanmelden bij je netbeheerder. De installatie is dan wel wettelijk verplicht door een erkend elektricien. Onze installatieservice regelt dit compleet voor je.',
  },
]

// ── Installatieservice funnel ─────────────────────────────────────────────────
const funnelStep       = ref(0)
const funnelStepLabels = ['Type', 'Situatie', 'Gegevens']
const submitting       = ref(false)

const form = ref({
  type:      '',
  situation: '',
  name:      '',
  postcode:  '',
  email:     '',
  phone:     '',
  note:      '',
})

const canSubmit = computed(() =>
  form.value.name.trim() &&
  form.value.postcode.trim() &&
  form.value.email.includes('@')
)

const installTypes = [
  { value: 'balkon',   icon: 'bi-building',          label: 'Balkonsysteem',    sub: 'Bevestiging op reling + aansluiting' },
  { value: 'dak',      icon: 'bi-house-fill',         label: 'Dakinstallatie',   sub: 'Schuin of plat dak, grotere systemen' },
  { value: 'laadpaal', icon: 'bi-lightning-charge',   label: 'Laadpaal',         sub: 'Thuisladen voor elektrische auto' },
  { value: 'overig',   icon: 'bi-three-dots',         label: 'Iets anders',      sub: 'Garage, schuur, grondopstelling…' },
]

const situationTypes = [
  { value: 'materiaal', label: 'Ik heb al materiaal van Plug2Charge',  sub: 'Ik heb een set gekocht en wil hulp bij installatie' },
  { value: 'advies',    label: 'Ik wil eerst advies en een offerte',   sub: 'Ik weet nog niet precies wat ik nodig heb' },
  { value: 'compleet',  label: 'Ik wil alles in één keer regelen',     sub: 'Materiaal + installatie als compleet pakket' },
]

const serviceUsps = [
  'Gecertificeerde installateurs met ervaring',
  'Inclusief aansluiting en app-configuratie',
  'Volledige garantie op installatie',
  'Afspraak binnen 5 werkdagen',
  'Achteraf betalen via factuur',
]

const submitError = ref('')
const hp = ref('')   // honeypot

const submitFunnel = async () => {
  if (!canSubmit.value || submitting.value) return
  submitting.value = true
  submitError.value = ''

  try {
    const res = await fetch('/api/installation-request', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ ...form.value, website: hp.value }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.error || 'Versturen mislukt. Probeer het opnieuw.')
    }
    funnelStep.value = 3   // bevestigingsstap
  } catch (e) {
    submitError.value = e.message || 'Er ging iets mis. Controleer je verbinding en probeer het opnieuw.'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.accordion-enter-active,
.accordion-leave-active { transition: all 0.22s ease; overflow: hidden; }
.accordion-enter-from,
.accordion-leave-to { opacity: 0; max-height: 0; }
.accordion-enter-to,
.accordion-leave-from { opacity: 1; max-height: 600px; }
</style>
